import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { get } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { FaTrash } from 'react-icons/fa';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import DataTable from '../../components/DataTable';

export default function UserCompany() {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const id = get(params, 'id', '');
  const [companies, setCompanies] = useState([]);
  const [user, setUser] = useState({ Companies: [], UserProfile: {} });
  const [formValues, setFormValues] = useState({
    user_id: '',
  });

  const validationSchema = Yup.object().shape({
    company_id: Yup.string().required('Empresa obrigatória.'),
  });

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Empresa',
        accessor: 'name',
      },
      {
        Header: 'Remover',
        accessor: 'remove',
        noSearch: true,
      },
    ],
    []
  );

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormValues((prevValues) => ({ ...prevValues, [fieldName]: value }));
  }, []);

  const fields = [
    {
      name: 'company_id',
      id: 'company_id',
      label: 'Empresa',
      type: 'select',
      options: companies,
      onChange: (e) =>
        handleFieldChange('company_id', e.value ? e.value : e.target.value),
      colSize: 12,
      additionalProps: {},
    },
  ];

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await axios.post(`/userscompanies`, {
        ...formValues,
        ...{ user_id: id },
      });
      const getUser = await axios.get(`users/${id}`);
      setUser(getUser.data);
      notification(`Empresa adicionada com sucesso`, 'success');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = useCallback(
    async (UserCompanyId) => {
      try {
        setIsLoading(true);
        await axios.delete(`/userscompanies/${UserCompanyId}`);
        const getUser = await axios.get(`users/${id}`);
        setUser(getUser.data);
        notification('Empresa removida do usuário com sucesso', 'success');
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, notification, id]
  );

  const tableData = useMemo(() => {
    return user.Companies.map((company) => ({
      remove: (
        <ButtonModal
          onConfirm={() => confirmDelete(company.UserCompany.id)}
          message={`Confirma remoção da empresa ${company.name} ?`}
          buttonProps={{
            variant: 'danger',
            className: 'rounded-pill shadow',
            size: 'sm',
          }}
          textModal={{
            title: 'Remover Empresa do usuário',
            textConfirm: 'Remover',
          }}
        >
          <FaTrash />
        </ButtonModal>
      ),
      ...company,
    }));
  }, [user, confirmDelete]);

  useEffect(() => {
    if (!id) {
      navigate('/users');
      notification(`Usuário inválido ou não informado`);
      return;
    }

    async function getData() {
      try {
        setIsLoading(true);
        const options = [{ value: '', label: '-- Selecione --' }];
        const getUser = await axios.get(`users/${id}`);
        const getCompanies = await axios.get(`companies`);

        console.log(getUser);
        setUser(getUser.data);

        if (getCompanies) {
          getCompanies.data.forEach((data) => {
            options.push({
              value: data.id,
              label: data.name,
            });
          });
        }
        setCompanies(options);
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    }

    getData();
  }, [id, notification, navigate]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title="Empresa"
        pages={[{ link: '/users', name: 'Usuários' }]}
      />
      <section>
        <Row>
          <Col lg={8}>
            <Card className="mb-4">
              <Card.Header>
                <h4 className="card-heading">
                  Empresas do usuário {user.UserProfile.name}
                </h4>
              </Card.Header>
              <Card.Body>
                <DataTable
                  items={tableData}
                  columns={tableColumns}
                  defaultPageSize={5}
                />
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4}>
            <Card className="mb-5">
              <Card.Header>
                <h4 className="card-heading">Adicionar Empresa</h4>
              </Card.Header>
              <Card.Body>
                <ErrorProcessor error={apiErrors} />
                {!id || dataLoaded ? (
                  <Forms
                    initialValues={formValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    fields={fields}
                    btnSubmit={{
                      variant: 'primary',
                      type: 'submit',
                      text: 'Adicionar',
                    }}
                  />
                ) : null}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
